import { useState, useEffect, useRef } from 'react'
import { gsap } from 'gsap'
import { Maximize2, Minimize2, ChevronDown } from 'lucide-react'
import { Drawer as VaulDrawer } from 'vaul'
import { useTranslation } from 'react-i18next'
import { cn } from '@/lib/utils'

/**
 * PacerDrawer — Mobile chat interface for the Pacer assistant.
 *
 * Opens as a vaul bottom drawer from the Pacer orb FAB.
 *
 * SNAP GEOMETRY (important — do not fight this):
 *   Vaul uses window.innerHeight for all offset math, then applies translateY DOWN.
 *   At snap 0.32: translateY = 0.68 × window.innerHeight → the TOP 32% of the drawer
 *   content is visible. That is why the input must be ABOVE the messages in the DOM.
 *
 *   The drawer height must be `100dvh` (h-dvh). Do NOT set an explicit pixel/svh height.
 *   svh ≠ window.innerHeight on iOS — setting height: 60svh caused a double-translation
 *   that pushed the drawer 80% off screen (tiny sliver).
 *
 * Layout order (required for partial snap):
 *   drag handle → header → input area → messages
 *   At 0.32 snap: input is visible; messages begin just below.
 *   At 1.0 snap: full screen; user scrolls messages below the input.
 *
 * Features:
 *   - Dark slate bg in both light and dark mode (Pacer always feels like night)
 *   - Rotating Pacer gradient border on textarea (ambient) → disappears on focus
 *   - Scrollable conversation thread below input: user (ocean) / Pacer (amber + slate)
 *   - Auto-grow textarea, Enter to send
 *   - Typing indicator (3-dot bounce)
 *   - Expand (snap 100%) / Collapse (snap 32%) / ChevronDown close
 *   - Swipe down → close. snapToSequentialPoint prevents velocity-snap-to-full.
 *   - In-session conversation history (cleared on close)
 *   - Real POST to pacer.swimflows.com/ask via fetch
 *
 * Phase: orb tap → opens this drawer on mobile.
 * Phase 8+: wire streaming responses.
 */

const PACER_URL = 'https://pacer.swimflows.com/ask'

interface Message {
  role: 'user' | 'pacer'
  text: string
}

interface HistoryItem {
  role: string
  content: string
}

interface PacerDrawerProps {
  open: boolean
  onClose: () => void
}

const EXAMPLE_PROMPTS = [
  '"What\'s the cheapest offer right now?"',
  '"Show me something for a music lover"',
  '"What can I get for 500 Drops?"',
  '"What streams are on SWIM?"',
]

function OrbDot() {
  return (
    <div
      className="w-4 h-4 rounded-full flex-shrink-0 relative overflow-hidden"
      style={{ background: 'linear-gradient(135deg, #fde68a 0%, #f59e0b 35%, #fb923c 65%, #e8dff5 100%)' }}
    >
      {/* CSS crossfade to second gradient — GPU-composited opacity, no JS repaint */}
      <div
        className="absolute inset-0 rounded-full"
        style={{
          background: 'linear-gradient(220deg, #fed7aa 0%, #fbbf24 30%, #e8dff5 60%, #a5f3fc 100%)',
          animation: 'pacerOrbDot 4s ease-in-out infinite',
        }}
      />
    </div>
  )
}

function TypingIndicator() {
  return (
    <div className="self-start flex gap-1.5 px-3 py-2.5 bg-slate-700/60 rounded-2xl rounded-bl-sm">
      {[0, 1, 2].map((i) => (
        <span
          key={i}
          className="w-1.5 h-1.5 rounded-full bg-slate-400 block"
          style={{ animation: `pacerDotBounce 1.2s ease-in-out infinite`, animationDelay: `${i * 0.18}s` }}
        />
      ))}
    </div>
  )
}

const SNAP_PARTIAL = 0.32
const SNAP_FULL = 1

export function PacerDrawer({ open, onClose }: PacerDrawerProps) {
  const { t: tCommon } = useTranslation('common')
  const { t } = useTranslation('pacer')
  const [snapPoint, setSnapPoint] = useState<number | string | null>(SNAP_PARTIAL)
  const [messages, setMessages] = useState<Message[]>([])
  const [inputVal, setInputVal] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [inputFocused, setInputFocused] = useState(false)
  const messagesRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLTextAreaElement>(null)
  const historyRef = useRef<HistoryItem[]>([])

  const isFullScreen = snapPoint === SNAP_FULL
  const prefersReduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches
  const typingRef = useRef<HTMLDivElement>(null)

  function handleClose() {
    onClose()
    setSnapPoint(SNAP_PARTIAL)
  }

  // Scroll messages to bottom whenever messages update
  useEffect(() => {
    if (messagesRef.current) {
      messagesRef.current.scrollTop = messagesRef.current.scrollHeight
    }
  }, [messages, isLoading])

  // Focus input when opened — delayed until Vaul's own slide-up transition has
  // settled (~300ms) so the drawer animates in first, THEN the keyboard opens,
  // instead of both firing at once (was 150ms, collided with the drawer's own
  // entrance animation and read as one jarring double-jump).
  useEffect(() => {
    if (open) {
      const timer = setTimeout(() => inputRef.current?.focus(), 350)
      return () => clearTimeout(timer)
    }
  }, [open])

  // Typing indicator show/hide
  useEffect(() => {
    const el = typingRef.current
    if (!el) return
    if (isLoading) {
      gsap.set(el, { display: 'block' })
      if (!prefersReduced) gsap.fromTo(el, { opacity: 0, y: 4 }, { opacity: 1, y: 0, duration: 0.25, ease: 'power2.out' })
    } else {
      if (!prefersReduced) {
        gsap.to(el, { opacity: 0, y: 4, duration: 0.18, ease: 'power2.in', onComplete: () => gsap.set(el, { display: 'none' }) })
      } else {
        gsap.set(el, { display: 'none' })
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading])

  async function submit() {
    const q = inputVal.trim()
    if (!q || isLoading) return

    setInputVal('')
    setMessages((prev) => [...prev, { role: 'user', text: q }])
    historyRef.current.push({ role: 'user', content: q })
    setIsLoading(true)

    try {
      const res = await fetch(PACER_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ question: q, messages: historyRef.current }),
      })
      if (!res.ok) throw new Error(`${res.status}`)
      const data = await res.json() as { answer?: string; error?: string }
      if (data.error) throw new Error(data.error)
      const answer = data.answer ?? '(no answer)'
      setMessages((prev) => [...prev, { role: 'pacer', text: answer }])
      historyRef.current.push({ role: 'assistant', content: answer })
    } catch (e) {
      console.error('[PacerDrawer] ask failed:', e)
      setMessages((prev) => [...prev, { role: 'pacer', text: t('drawer.error') }])
    } finally {
      setIsLoading(false)
      setTimeout(() => inputRef.current?.focus(), 50)
    }
  }

  return (
    <>
      {/* Keyframes */}
      <style>{`
        @keyframes pacerGradientSpin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        .pacer-ring-spin { animation: pacerGradientSpin 3s linear infinite; }
        @media (prefers-reduced-motion: reduce) {
          .pacer-ring-spin { animation: none; }
        }
        @keyframes pacerOrbDot {
          0%, 100% { opacity: 0; }
          50% { opacity: 1; }
        }
      `}</style>

      <VaulDrawer.Root
        open={open}
        onOpenChange={(o) => { if (!o) handleClose() }}
        snapPoints={[SNAP_PARTIAL, SNAP_FULL]}
        activeSnapPoint={snapPoint}
        setActiveSnapPoint={setSnapPoint}
        shouldScaleBackground
        snapToSequentialPoint
      >
        <VaulDrawer.Portal>
          <VaulDrawer.Overlay className="fixed inset-0 z-50 bg-black/70" />

          {/*
            Drawer height: always h-dvh (100dvh). Vaul positions via translateY only.
            Do NOT set an explicit pixel/svh height — svh ≠ window.innerHeight on iOS
            and causes a double-translation that makes the drawer nearly invisible.
            At SNAP_PARTIAL (0.32): vaul translateY = 0.68 × window.innerHeight,
            revealing the TOP 32% of this element. Layout order puts input in that zone.
          */}
          <VaulDrawer.Content
            aria-label={t('drawer.title')}
            aria-modal="true"
            className={cn(
              'fixed inset-x-0 bottom-0 z-50 flex flex-col outline-none',
              'bg-slate-900 overflow-hidden h-dvh',
              isFullScreen ? 'rounded-none' : 'rounded-t-2xl',
            )}
          >
            {/* ── Pacer ambient glow ── */}
            <div
              aria-hidden
              className="absolute inset-0 pointer-events-none"
              style={{
                background: [
                  'radial-gradient(ellipse at 15% 90%, rgba(251,191,36,0.07) 0%, transparent 55%)',
                  'radial-gradient(ellipse at 85% 10%, rgba(14,165,233,0.06) 0%, transparent 55%)',
                  'radial-gradient(ellipse at 50% 50%, rgba(251,146,60,0.04) 0%, transparent 65%)',
                ].join(', '),
              }}
            />

            {/* ── Drag handle ── */}
            <div className="mx-auto mt-3 mb-1 h-1 w-10 rounded-full bg-slate-700 flex-shrink-0" />

            {/* ── Header ── */}
            <div className="flex items-center gap-2.5 px-4 py-2.5 flex-shrink-0 border-b border-slate-800">
              <OrbDot />
              <span className="flex-1 text-sm font-semibold font-sans text-white">
                {t('drawer.title')}
              </span>
              <button
                onClick={() => setSnapPoint(isFullScreen ? SNAP_PARTIAL : SNAP_FULL)}
                aria-label={isFullScreen ? t('drawer.collapse') : t('drawer.expand')}
                className="
                  w-11 h-11 flex items-center justify-center rounded-md
                  text-slate-400 hover:text-white hover:bg-slate-700
                  transition-colors
                "
              >
                {isFullScreen
                  ? <Minimize2 className="w-3.5 h-3.5" aria-hidden />
                  : <Maximize2 className="w-3.5 h-3.5" aria-hidden />
                }
              </button>
              <button
                onClick={handleClose}
                aria-label={t('drawer.close')}
                className="
                  w-11 h-11 flex items-center justify-center rounded-md
                  text-slate-400 hover:text-white hover:bg-slate-700
                  transition-colors
                "
              >
                <ChevronDown className="w-4 h-4" aria-hidden />
              </button>
            </div>

            {/*
              ── Input area — ABOVE messages ──
              This must stay above messages so it falls within the visible top 32%
              at SNAP_PARTIAL. Do not move it below the messages div.
            */}
            <div className="flex-shrink-0 px-4 pt-3 pb-2 border-b border-slate-800/60">
              {/* Rotating gradient border wrapper — ring hides on focus, never unmounts */}
              <div className="relative rounded-xl p-[1.5px] overflow-hidden">
                {/* Spinning gradient ring (ambient state only). Stays mounted so the spin
                    phase is kept; focus pauses + hides it. 150vmax square covers the
                    wrapper's diagonal at every rotation angle. */}
                <div
                  aria-hidden
                  className="pacer-ring-spin absolute"
                  style={{
                    background: 'conic-gradient(from 0deg, #fde68a, #f59e0b, #fb923c, #e8dff5, #a5f3fc, #fde68a)',
                    left: '50%',
                    top: '50%',
                    width: '150vmax',
                    height: '150vmax',
                    marginLeft: '-75vmax',
                    marginTop: '-75vmax',
                    opacity: inputFocused || prefersReduced ? 0 : 1,
                    transition: 'opacity 150ms',
                    animationPlayState: inputFocused ? 'paused' : 'running',
                  }}
                />
                {/* Textarea sits on top of the gradient ring */}
                <div className="relative">
                  <textarea
                    ref={inputRef}
                    value={inputVal}
                    onChange={(e) => {
                      setInputVal(e.target.value)
                      e.target.style.height = 'auto'
                      e.target.style.height = `${e.target.scrollHeight}px`
                    }}
                    onFocus={() => setInputFocused(true)}
                    onBlur={() => setInputFocused(false)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && !e.metaKey && !e.ctrlKey) {
                        e.preventDefault()
                        void submit()
                      }
                    }}
                    placeholder={t('drawer.placeholder')}
                    rows={3}
                    disabled={isLoading}
                    className="
                      w-full bg-slate-800 rounded-[10px] px-3 py-2.5
                      text-sm text-white placeholder:text-slate-500
                      border border-slate-700 focus:border-amber-500/50
                      focus:outline-none resize-none font-sans leading-relaxed
                      disabled:opacity-50 max-h-28 overflow-y-auto
                      transition-colors duration-150
                    "
                  />
                </div>
              </div>
              <div className="flex items-center justify-between mt-1.5">
                <span className={cn(
                  'text-[10px] font-sans transition-colors duration-150',
                  isLoading ? 'text-amber-400' : 'text-slate-500',
                )}>
                  {isLoading ? t('drawer.thinking') : t('drawer.status')}
                </span>
                <span className="text-[10px] font-mono text-slate-600">
                  {t('drawer.hint')}
                </span>
              </div>
            </div>

            {/* ── Messages — below input ── */}
            <div
              ref={messagesRef}
              className="flex-1 min-h-0 overflow-y-auto px-4 py-4 flex flex-col gap-3 scroll-smooth"
              aria-live="polite"
              aria-label={tCommon('aria.conversation')}
            >
              {messages.length === 0 && !isLoading && (
                <div className="flex flex-col gap-3 my-auto py-6">
                  <p className="text-sm font-sans text-slate-400 text-center leading-relaxed">
                    {t('drawer.emptyState')}
                  </p>
                  <div className="flex flex-col gap-1.5">
                    {EXAMPLE_PROMPTS.map((prompt) => (
                      <p
                        key={prompt}
                        className="text-[11px] font-mono text-slate-600 text-center leading-relaxed"
                      >
                        {prompt}
                      </p>
                    ))}
                  </div>
                </div>
              )}

              {messages.map((msg, i) => (
                <div
                  key={i}
                  className={cn(
                    'flex flex-col gap-0.5 max-w-[88%]',
                    msg.role === 'user' ? 'self-end items-end' : 'self-start items-start',
                  )}
                >
                  <span className={cn(
                    'text-[10px] font-mono uppercase tracking-wider px-1',
                    msg.role === 'user' ? 'text-ocean-400' : 'text-amber-400',
                  )}>
                    {msg.role === 'user' ? t('drawer.you') : t('drawer.pacer')}
                  </span>
                  <div className={cn(
                    'px-3 py-2 rounded-2xl text-sm font-sans leading-relaxed whitespace-pre-wrap break-words',
                    msg.role === 'user'
                      ? 'bg-ocean-600/30 text-white rounded-br-sm'
                      : 'bg-slate-700/60 text-sand-100 rounded-bl-sm',
                  )}>
                    {msg.text}
                  </div>
                </div>
              ))}

              <div ref={typingRef} style={{ display: 'none' }}>
                <TypingIndicator />
              </div>
            </div>
          </VaulDrawer.Content>
        </VaulDrawer.Portal>
      </VaulDrawer.Root>
    </>
  )
}
